import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function IncidentTypeAllocation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const memberId = searchParams.get("member_id") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [guard, setGuard] = useState(null);
  const [types, setTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    if (!memberId) {
      toast.error("Missing member_id.");
      setLoading(false);
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate, memberId]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const [dashboardRes, permissionRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`${API_BASE_URL}/incident-permissions/${memberId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!permissionRes.ok) throw new Error("Failed to fetch incident permissions");

      const dashboardResult = await dashboardRes.json();
      const permissionResult = await permissionRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      if (!permissionResult?.success) {
        throw new Error(permissionResult?.message || "Failed to load data");
      }

      setGuard(permissionResult.guard || null);
      setTypes(permissionResult.types || []);
      setSelectedIds((permissionResult.selectedIds || []).map(Number));
    } catch (error) {
      console.error("Incident permission fetch error:", error);
      toast.error(error.message || "Failed to load incident permissions.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const filteredTypes = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return types.filter((item) => {
      if (!search) return true;

      return (
        String(item.label || "").toLowerCase().includes(search) ||
        String(item.type_key || "").toLowerCase().includes(search)
      );
    });
  }, [types, searchTerm]);

  const toggleType = (id) => {
    const typeId = Number(id);

    setSelectedIds((prev) =>
      prev.includes(typeId)
        ? prev.filter((item) => item !== typeId)
        : [...prev, typeId]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = filteredTypes.map((item) => Number(item.id));
    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const clearVisible = () => {
    const visibleIds = filteredTypes.map((item) => Number(item.id));
    setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
  };

  const selectOnlyActive = () => {
    const activeIds = types
      .filter((item) => Number(item.is_active) === 1 || item.is_active === true)
      .map((item) => Number(item.id));

    setSelectedIds(activeIds);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/incident-permissions/${memberId}/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          member_id: memberId,
          type_ids: selectedIds,
        }),
      });

      const result = await res.json();

      if (!res.ok || !result?.success) {
        throw new Error(result?.message || "Save failed");
      }

      toast.success(result.message || "Incident permissions saved successfully.");
      setTimeout(() => {
        navigate("/add_incidents");
      }, 800);
    } catch (error) {
      console.error("Incident permission save error:", error);
      toast.error(error.message || "Failed to save incident permissions.");
    } finally {
      setSaving(false);
    }
  };

  const backToUsers = () => navigate("/add_incidents");

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading incident permissions…
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="user-list"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />
      
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Incident Permission Management
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Assign incident types to selected guard
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Incident Type Allocation
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Select the incident categories this user can access
              </p>
            </div>
            <button
              type="button"
              onClick={backToUsers}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Back to Users
            </button>
          </div>
        </div>

        {/* Guard Card */}
        {guard && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <h3 className="text-[20px] font-extrabold text-[var(--text-primary)] m-0 mb-2">
              {guard.name || "User"}{" "}
              <span className="text-[13px] text-[var(--text-muted)] font-normal">
                · {memberId}
              </span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[13px] text-[var(--text-primary)]">
              <div>
                <span className="font-extrabold text-[var(--text-sub)]">Username:</span> {guard.username || "—"}
              </div>
              <div>
                <span className="font-extrabold text-[var(--text-sub)]">Role:</span> {guard.role || "Guard"}
              </div>
              <div>
                <span className="font-extrabold text-[var(--text-sub)]">Status:</span> {guard.status || "—"}
              </div>
            </div>
          </div>
        )}

        {/* Filter Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[2fr_auto_auto_auto]">
            <input
              type="text"
              placeholder="Search incident type or key..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <button
              type="button"
              onClick={selectAllVisible}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={clearVisible}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={selectOnlyActive}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
            >
              Only Active
            </button>
          </div>
        </div>

        {/* Incident Grid */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div
            className="px-6 py-4 text-[13px] font-extrabold tracking-wide text-white"
            style={{ background: "var(--btn-bg)" }}
          >
            Incident Types
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-6">
            {types.length === 0 ? (
              <div className="col-span-full py-8 px-5 rounded-xl border border-dashed border-[var(--surface-border)] text-center text-[13px] text-[var(--text-muted)] font-semibold">
                No incident types found.
              </div>
            ) : (
              filteredTypes.map((item) => {
                const id = Number(item.id);
                const checked = selectedIds.includes(id);
                const inactive = Number(item.is_active) !== 1 && item.is_active !== true;

                return (
                  <label
                    key={id}
                    className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-4 transition-colors ${
                      checked
                        ? "border-[var(--accent-border)] bg-[var(--accent-faint)]"
                        : "border-[var(--surface-border)] bg-[var(--card-bg)] hover:bg-[var(--accent-faint)]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleType(id)}
                      className="w-5 h-5 rounded border-[var(--surface-border)] accent-[var(--accent)] cursor-pointer"
                    />

                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg font-extrabold text-white"
                      style={{
                        background: item.color || "var(--btn-bg)",
                      }}
                    >
                      {item.icon_value || "!"}
                    </div>

                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-extrabold text-[var(--text-primary)]">
                        {item.label || "—"}
                      </div>
                      <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                        {item.type_key || "—"}
                        {inactive && " · inactive"}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={backToUsers}
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            style={{
              background: "var(--btn-bg)",
              color: "var(--btn-text)",
              boxShadow: "0 4px 18px var(--accent-glow)",
            }}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        </div>
      </div>
    </Layout>
  );
}